import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { TextField } from '@mui/material';
import { validInteger } from 'app/helpers/numberHelper';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useSelector } from 'react-redux';
import { selectIsEditMod, selectIsEmiLmoDisabled } from 'store/redux-store/placement-mods/selectors';

export const LmoNumberOfDays = () => {
  const { control, formState: { errors }, getValues, setValue, clearErrors, setError } = useFormContext();
  const { t } = useTranslation();
  const moveOutDate: Date | string | null | undefined = useWatch({ name: 'moveOutDate' });
  const isModEdit = useSelector(selectIsEditMod);
  const { promiseInProgress: isSaveModApiCalling } = usePromiseTracker({ area: 'save-mod-api', delay: 0 });
  const lmoDaysWaived: number | string | null | undefined = useWatch({ name: 'lmoDaysWaived' });
  const lmoCostPerDay: number | string | null | undefined = useWatch({ name: 'lmoCostPerDay' });
  const lateMoveOutDate: Date | string | null | undefined = useWatch({ name: 'lateMoveOutDate' });
  const lmoNumberOfDays: number | string | null | undefined = useWatch({ name: 'lmoNumberOfDays' });
  const isEmiLmoDisabled = useSelector(selectIsEmiLmoDisabled);
  const [required, setRequired] = useState<boolean>();
  const checkDaysValidation = currentLmoNumberOfDays => {
    if (currentLmoNumberOfDays && !isNullOrUndefined(lmoDaysWaived) && !isEmpty(lmoDaysWaived)) {
      if (Number(currentLmoNumberOfDays) < Number(lmoDaysWaived)) {
        return t('placement.placementMods.daysWaivedError');
      }
    }
    const modifiedDate = isNullOrUndefined(moveOutDate)
      ? null
      : moment(moveOutDate).add(currentLmoNumberOfDays, 'days');
    setValue(
      'lateMoveOutDate',
      isEmpty(currentLmoNumberOfDays) || isNullOrUndefined(currentLmoNumberOfDays)
        ? ''
        : isNullOrUndefined(moveOutDate)
        ? ''
        : modifiedDate.format(),
      { shouldDirty: false },
    );
    clearErrors('lmoNumberOfDays');
    clearErrors('lateMoveOutDate');
    return null;
  };

  useEffect(() => {
    if (!isNullOrUndefined(lateMoveOutDate) && lateMoveOutDate !== '') {
      setValue(
        'lateMoveOutDate',
        isNullOrUndefined(lmoNumberOfDays)
          ? ''
          : isNullOrUndefined(moveOutDate)
          ? ''
          : moment(moveOutDate).add(lmoNumberOfDays, 'days'),
        { shouldDirty: false },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (!isNullOrUndefined(lateMoveOutDate) && !isEmpty(lateMoveOutDate)) ||
      (!isNullOrUndefined(getValues('lmoCostPerDay')) && getValues('lmoCostPerDay') !== '') ||
      (!isNullOrUndefined(getValues('lmoNumberOfDays')) && getValues('lmoNumberOfDays') !== '') ||
      (!isNullOrUndefined(getValues('lmoDaysWaived')) && getValues('lmoDaysWaived') !== '')
    ) {
      if (isNullOrUndefined(lmoNumberOfDays) || lmoNumberOfDays === '') {
        clearErrors('lmoNumberOfDays');
        setError('lmoNumberOfDays', t('placement.placementMods.numberOfDaysError'));
      } else {
        if (lmoNumberOfDays && lmoDaysWaived) {
          if (Number(lmoDaysWaived) <= Number(lmoNumberOfDays)) {
            clearErrors('lmoNumberOfDays');
          }
        }
      }
      setRequired(true);
    } else {
      setRequired(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lateMoveOutDate, lmoCostPerDay, lmoDaysWaived, lmoNumberOfDays]);

  useEffect(() => {
    if (!required) {
      setTimeout(() => {
        clearErrors('lmoNumberOfDays');
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required]);

  return (
    <Controller
      control={control}
      name={'lmoNumberOfDays'}
      rules={{
        validate: checkDaysValidation,
        required: required,
      }}
      render={({ field: { onChange, value, ...rest } }) => (
        <TextField
          value={lmoNumberOfDays}
          variant="filled"
          fullWidth
          id="lmoNumberOfDays"
          color="primary"
          label={t('placement.placementMods.numberofDays')}
          InputProps={{
            readOnly: !isModEdit || isEmiLmoDisabled,
            style: {
              pointerEvents: (isModEdit || isSaveModApiCalling) && !isEmiLmoDisabled ? 'auto' : 'none',
            },
          }}
          onChange={e => {
            if (validInteger(e.target.value, { numberLength: 4 })) {
              setValue('lmoNumberOfDays', e.target.value, { shouldDirty: true });
              onChange(e.target.value);
              setValue(
                'lateMoveOutDate',
                isNullOrUndefined(e.target.value) || e.target.value === ''
                  ? null
                  : isNullOrUndefined(moveOutDate)
                  ? null
                  : moment(moveOutDate).add(e.target.value, 'days'),
                { shouldDirty: true },
              );
            } else {
              setValue('lateMoveOutDate', '', { shouldDirty: true });
            }
            clearErrors('lateMoveOutDate');
          }}
          {...rest}
          InputLabelProps={{
            shrink: lmoNumberOfDays ? true : null,
            sx: { marginTop: lmoNumberOfDays ? '0px' : '-10px' },
          }}
          disabled={isSaveModApiCalling}
          required={required}
          error={!!errors?.lmoNumberOfDays}
          helperText={!isNullOrUndefined(errors?.lmoNumberOfDays) ? errors?.lmoNumberOfDays?.message : undefined}
        />
      )}
    />
  );
};
